import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronDown, Hexagon } from "lucide-react";
import { IMAGES, ECOSYSTEM } from "@/data/site";

const line1 = "Building Industry.".split(" ");
const line2 = "Enabling Technology.".split(" ");

const wordVariant = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 0.9, delay: 0.15 + i * 0.09, ease: [0.22, 1, 0.36, 1] },
  }),
};

export function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "24%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.62, 0.85]);

  return (
    <section ref={ref} className="relative min-h-[100svh] flex items-end overflow-hidden bg-automex-navy" data-testid="hero-section">
      <motion.div style={{ y, scale }} className="absolute inset-0 z-0">
        <img
          src={IMAGES.hero}
          alt="Heavy industrial machinery and overhead crane inside a modern manufacturing facility"
          className="h-full w-full object-cover"
        />
      </motion.div>
      <motion.div style={{ opacity: overlayOpacity }} className="absolute inset-0 z-10 bg-automex-navy" aria-hidden="true" />
      <div className="absolute inset-0 z-10 bg-gradient-to-t from-automex-navy via-automex-navy/30 to-transparent" aria-hidden="true" />

      <div className="relative z-20 mx-auto w-full max-w-[1400px] px-5 sm:px-8 pb-16 md:pb-24 pt-32">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.22em] text-white mb-7"
        >
          <Hexagon className="h-3.5 w-3.5 fill-automex-teal/30 text-automex-teal" aria-hidden="true" />
          <span className="h-px w-8 bg-automex-teal" />
          Industrial Infrastructure &amp; Technology
        </motion.div>

        <h1 className="font-heading font-black uppercase tracking-tighter text-white leading-[0.88] text-5xl sm:text-7xl md:text-8xl">
          <span className="block overflow-hidden pb-1">
            {line1.map((w, i) => (
              <span key={w} className="inline-block overflow-hidden align-bottom">
                <motion.span custom={i} variants={wordVariant} initial="hidden" animate="show" className="inline-block mr-[0.25em]">
                  {w}
                </motion.span>
              </span>
            ))}
          </span>
          <span className="block overflow-hidden pb-1 text-automex-teal">
            {line2.map((w, i) => (
              <span key={w} className="inline-block overflow-hidden align-bottom">
                <motion.span custom={i + line1.length} variants={wordVariant} initial="hidden" animate="show" className="inline-block mr-[0.25em]">
                  {w}
                </motion.span>
              </span>
            ))}
          </span>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.7 }}
          className="mt-7 max-w-2xl text-base sm:text-lg text-slate-200 leading-relaxed"
        >
          Industrial infrastructure, project execution, machinery, material handling and technology
          solutions for modern manufacturing.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.85 }}
          className="mt-9 flex flex-col sm:flex-row gap-4"
        >
          <Link
            to="/contact"
            data-testid="hero-discuss-cta"
            className="group inline-flex items-center justify-center gap-2.5 bg-automex-teal text-automex-navy px-8 py-4 text-sm font-bold uppercase tracking-wider rounded-sm hover:bg-[#12b494] transition-colors"
          >
            Discuss Your Project
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Link>
          <Link
            to="/solutions"
            data-testid="hero-explore-cta"
            className="group inline-flex items-center justify-center gap-2.5 bg-automex-navy/40 backdrop-blur-sm border border-white text-white px-8 py-4 text-sm font-bold uppercase tracking-wider rounded-sm hover:bg-white hover:text-automex-navy transition-colors"
          >
            Explore Solutions
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Link>
        </motion.div>

        <motion.div
          initial="hidden"
          animate="show"
          transition={{ staggerChildren: 0.12, delayChildren: 1.0 }}
          className="mt-14 flex flex-wrap items-center gap-x-4 gap-y-3 border-t border-white/15 pt-6 text-sm sm:text-base font-semibold uppercase tracking-[0.18em] text-slate-200"
        >
          {ECOSYSTEM.map((step, i) => (
            <motion.span
              key={step}
              variants={{ hidden: { opacity: 0, y: 10 }, show: { opacity: 1, y: 0 } }}
              className="flex items-center gap-4"
            >
              <span className={i === 0 ? "text-automex-teal" : "text-white"}>{step}</span>
              {i < ECOSYSTEM.length - 1 && (
                <motion.span
                  className="text-automex-teal"
                  animate={{ x: [0, 4, 0] }}
                  transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut", delay: i * 0.1 }}
                >
                  →
                </motion.span>
              )}
            </motion.span>
          ))}
        </motion.div>
      </div>

      <div className="absolute bottom-6 right-6 z-20 hidden md:flex items-center gap-2 text-white/50 text-xs uppercase tracking-widest">
        Scroll <ChevronDown className="h-4 w-4 animate-bounce" />
      </div>
    </section>
  );
}
